/**
 * accessibility.js — Global Accessibility Controller
 *
 * Provides:
 * - Screen reader live region (announce) for dynamic content
 * - Modal focus trap and keyboard (Escape, Tab) handling
 * - Consistent ARIA semantics for popups
 *
 * Used by: search-bar.js, popups.js, pills-block.js
 */
(function () {
  'use strict';

  const LIVE_REGION_ID = 'bfwt-a11y-announcer';
  /** Standard delay so screen readers re-announce when the same message is set again. */
  const ANNOUNCE_DELAY_MS = 100;

  window.BFWT_Accessibility = {
    // --- State (internal) ---
    activeModal: null,
    triggerElement: null,
    _trapFocusHandler: null,
    firstFocusable: null,
    lastFocusable: null,
    _initialized: false,

    /**
     * Initialize the accessibility layer: live region and global Escape handler.
     * Safe to call multiple times; runs once. Runs when DOM is ready or immediately if body exists.
     */
    init: function () {
      if (this._initialized) return;
      if (!document.body) {
        document.addEventListener('DOMContentLoaded', () => this.init());
        return;
      }
      this._createLiveRegion();
      const main = document.getElementById('main');
      if (main && !main.hasAttribute('tabindex'))
        {main.setAttribute('tabindex', '-1');}
      this._trapFocusHandler = this.trapFocus.bind(this);
      document.addEventListener('keydown', this.handleGlobalKeys.bind(this));
      this._initialized = true;
    },

    /**
     * Create the off-screen live region for screen reader announcements.
     * @private
     */
    _createLiveRegion: function () {
      if (document.getElementById(LIVE_REGION_ID)) return;

      const liveRegion = document.createElement('div');
      liveRegion.id = LIVE_REGION_ID;
      liveRegion.className = 'screen-reader-text';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('aria-relevant', 'text');
      Object.assign(liveRegion.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
        border: '0',
      });

      document.body.appendChild(liveRegion);
    },

    /**
     * Announce a message to screen readers via the live region.
     * @param {string} message - Text to announce.
     * @param {'polite'|'assertive'} [politeness='polite'] - aria-live value.
     */
    announce: function (message, politeness = 'polite') {
      const region = document.getElementById(LIVE_REGION_ID);
      if (!region || message === null || message === undefined) return;

      region.textContent = '';
      region.setAttribute(
        'aria-live',
        politeness === 'assertive' ? 'assertive' : 'polite'
      );

      setTimeout(() => {
        region.textContent = String(message);
      }, ANNOUNCE_DELAY_MS);
    },

    /**
     * Global key handler: Escape closes the active modal (if BFWT_Popups is available).
     * @param {KeyboardEvent} e
     */
    handleGlobalKeys: function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && this.activeModal) {
        if (
          window.BFWT_Popups &&
          typeof window.BFWT_Popups.closeModal === 'function'
        ) {
          window.BFWT_Popups.closeModal(this.activeModal);
        }
      }
    },

    /**
     * Set up accessibility for an open modal: role=dialog, aria-modal, focus trap, first focus.
     * Call after the modal is in the DOM and visible.
     * @param {HTMLElement} modal - The overlay element (e.g. .bfwt-popup-overlay).
     * @param {HTMLElement|null} triggerElement - Element to return focus to when modal closes.
     */
    initModalAccessibility: function (modal, triggerElement) {
      if (!modal) return;

      const popupBox = modal.querySelector('.bfwt-popup-box');
      if (!popupBox) return;

      this.activeModal = modal;
      this.triggerElement =
        triggerElement && triggerElement.nodeType === 1 ? triggerElement : null;

      if (!popupBox.hasAttribute('role'))
        {popupBox.setAttribute('role', 'dialog');}
      if (!popupBox.hasAttribute('aria-modal'))
        {popupBox.setAttribute('aria-modal', 'true');}

      const popupTitle = modal.querySelector('.bfwt-popup-title');
      if (popupTitle) {
        if (!popupTitle.id) popupTitle.id = 'bfwt-popup-title-' + Date.now();
        popupBox.setAttribute('aria-labelledby', popupTitle.id);
      }

      const focusableSelector =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const focusables = Array.from(
        modal.querySelectorAll(focusableSelector)
      ).filter((el) => {
        if (el.offsetWidth === 0 && el.offsetHeight === 0) return false;
        return (
          window.getComputedStyle(el).visibility !== 'hidden' &&
          window.getComputedStyle(el).display !== 'none'
        );
      });

      if (focusables.length === 0) {
        popupBox.setAttribute('tabindex', '-1');
        popupBox.focus();
        this.firstFocusable = popupBox;
        this.lastFocusable = popupBox;
      } else {
        this.firstFocusable = focusables[0];
        this.lastFocusable = focusables[focusables.length - 1];
        this.firstFocusable.focus();
      }

      modal.addEventListener('keydown', this._trapFocusHandler);
    },

    /**
     * Trap Tab/Shift+Tab inside the active modal.
     * @param {KeyboardEvent} e
     */
    trapFocus: function (e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusable) {
          this.lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === this.lastFocusable) {
          this.firstFocusable.focus();
          e.preventDefault();
        }
      }
    },

    /**
     * Tear down modal accessibility and return focus to the trigger element.
     */
    removeModalAccessibility: function () {
      if (this.activeModal) {
        this.activeModal.removeEventListener('keydown', this._trapFocusHandler);
      }
      if (
        this.triggerElement &&
        typeof this.triggerElement.focus === 'function'
      ) {
        try {
          this.triggerElement.focus();
        } catch (_) {}
      }

      this.activeModal = null;
      this.triggerElement = null;
      this.firstFocusable = null;
      this.lastFocusable = null;
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () =>
      window.BFWT_Accessibility.init()
    );
  } else {
    window.BFWT_Accessibility.init();
  }
})();
